import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { Field, ErrorMessage, useFormik, FormikProvider } from "formik";
import * as Yup from "yup";

import "./AddressForm.css";
import styles from "./AddressModalStyle.module.css";
import { useEffect, useState } from "react";
import moment from "moment";
import { usStates } from "../../constants/usaStates";
import { getPolicyAddresses } from "../../services/addresses";
import {
  checkDuplicate,
  createAddress,
  updateAddress,
} from "../../redux/features/addressSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

const validationSchema = Yup.object().shape({
  address_line_1: Yup.string().required("Address line 1 is required"),
  address_line_2: Yup.string(),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zip: Yup.string().required("Zip code is required"),
});

const initialValues = {
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  zip: "",
};

const AddressForm1 = ({
  show,
  onClose,
  policy_number,
  id,
  claims = [],
  effective_date,
}) => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.auth.userDetails);
  const { role: userRole } = userDetails;
  const [addresses, setAddresses] = useState([]);
  const [edit, setEdit] = useState(null);
  const [duplicateAddress, setDuplicateAddress] = useState(false);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      const _data = { ...values, country: "USA" };
      const created_at = moment().format("MM/DD/YY");
      // Check for duplicates only on create
      try {
        setLoading("checking");
        const duplicateResult = await dispatch(
          checkDuplicate({ policyId: id, data: _data }),
        ).unwrap();
        const addressExists = addresses.some(
          (addr) =>
            (addr.isNew || addr.isUpdated) &&
            addr.address_line_1?.trim().toLowerCase() ===
              values.address_line_1?.trim().toLowerCase() &&
            addr.address_line_2?.trim().toLowerCase() ===
              values.address_line_2?.trim().toLowerCase() &&
            addr.city?.trim().toLowerCase() ===
              values.city?.trim().toLowerCase() &&
            addr.state?.trim().toLowerCase() ===
              values.state?.trim().toLowerCase() &&
            addr.zip?.trim() === values.zip?.trim(),
        );

        if (duplicateResult.is_duplicate || addressExists) {
          // setDuplicateAddress({ ..._data, created_at, isNew: true });
          setDuplicateAddress(true);
          return;
        }
      } catch (error) {
        console.error("Error checking duplicate:", error);
      } finally {
        setLoading(false);
      }

      if (edit?.id || edit?.temp_id) {
        setEdit(null);
        setAddresses((prev) =>
          prev.map((el) =>
            (edit?.temp_id && el.temp_id === edit?.temp_id) ||
            el.id === edit?.id
              ? { ...edit, ..._data }
              : el,
          ),
        );
      } else {
        setAddresses((prev) => [
          ...prev,
          {
            ..._data,
            created_at,
            isNew: true,
            temp_id: crypto.randomUUID(),
          },
        ]);
      }
      resetForm();
    },
  });

  const fetchAddress = async () => {
    setLoading("fetching");
    const data = await getPolicyAddresses({ policy_id: id });
    setAddresses(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchAddress();
  }, []);

  const handleSave = async () => {
    setLoading("saving");
    const newAddresses = addresses.filter((addr) => addr?.isNew);
    const updateAddresses = addresses.filter(
      (addr) => !addr?.isNew && addr?.isUpdated && addr?.id,
    );

    for (const addr of newAddresses) {
     await dispatch(
        createAddress({
          policyId: id,
          data: { ...addr, isNew: undefined, temp_id: undefined },
        }),
      ).unwrap();
    }

    for (const addr of updateAddresses) {
      await dispatch(
        updateAddress({
          policyId: id,
          addressId: addr.id,
          data: { ...addr, isNew: undefined, isUpdated: undefined },
        }),
      ).unwrap();
    }
    toast.success("Address(es) upserted successfully");
    setLoading(false);
    onClose();
  };

  const closeWarningModal = () => {
    setDuplicateAddress(null);
  };

  const handleEdit = (row) => {
    setEdit(row);
    formik.setValues({
      ...row,
      isUpdated: true,
    });
  };

  const { address_line_1, city, state, zip } = formik.values;
  const isEnable = address_line_1 && city && state && zip;

  return (
    <>
      <Modal show={show} onHide={onClose} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title className={`${styles.address_modal_title}`}>
            Policy{" "}
            <span className={styles.address_modal_title_sub}>
              #{policy_number}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className={styles.address_header}>
            <Col>
              Effective Date: {moment(effective_date).format("MM/DD/YYYY")}
            </Col>
          </Row>
          <Row className={styles.address_header}>
            <Col>Claims</Col>
          </Row>
          <div className={styles.clainms_container_wrapper}>
            <div className={styles.claims_container}>
              {claims?.length > 0 ? (
                claims?.map((el) => (
                  <Row className={styles.address_item}>
                    <Col>{el}</Col>
                  </Row>
                ))
              ) : (
                <div className={styles.not_found}>
                  <p className="m-0">No associated claim found.</p>
                </div>
              )}
            </div>
          </div>

          <Row className={styles.address_header}>
            <Col>Address</Col>
            <Col className="text-end">Address Created</Col>
          </Row>

          <div className={styles.address_container}>
            {loading === "fetching" ? (
              <div className={styles.not_found}>
                <Spinner animation="grow" size="lg" />
              </div>
            ) : addresses?.length > 0 ? (
              addresses?.map((el, ind) => (
                <Row
                  className={`${styles.address_item} cursor-pointer`}
                  key={`item_${ind}`}
                  onClick={() => handleEdit(el)}
                >
                  <Col>
                    {el?.address_line_1}. {el?.city}, {el?.state} {el?.zip}
                  </Col>
                  {el?.created_at && (
                    <Col className="text-end">
                      {moment(el?.created_at).format("MM/DD/YY")}
                    </Col>
                  )}
                </Row>
              ))
            ) : (
              <div className={styles.not_found}>
                <p className="m-0">
                  Fill in the address information to add it to the address list.
                </p>
              </div>
            )}
          </div>

        

          {(userRole === "Admin" || userRole === "Tradesman") && (
            <>
              <div className={styles.address_header}>
                <p>Project Address*</p>
              </div>
              <FormikProvider value={formik}>
                <form onSubmit={formik.handleSubmit}>
                  <div className="form-group">
                    <Field
                      type="text"
                      id="address_line_1"
                      name="address_line_1"
                      className="form-control"
                      placeholder="Street Address Line 1"
                    />
                    <ErrorMessage
                      name="address_line_1"
                      component="div"
                      className="error-message"
                    />
                  </div>

                  <div className="form-group">
                    <Field
                      type="text"
                      id="address_line_2"
                      name="address_line_2"
                      className="form-control"
                      placeholder="Street Address Line 2"
                    />
                    <ErrorMessage
                      name="address_line_2"
                      component="div"
                      className="error-message"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <Field
                        type="text"
                        id="city"
                        name="city"
                        className="form-control"
                        placeholder="Enter city"
                      />
                      <ErrorMessage
                        name="city"
                        component="div"
                        className="error-message"
                      />
                    </div>

                    <div className="form-group">
                      <Field
                        component="select"
                        id="state"
                        name="state"
                        className={`form-control ${!state && styles.state_field}`}
                        placeholder="State"
                      >
                        <option value={""}>State</option>
                        {usStates.map((state) => (
                          <option key={state.id} value={state.value}>
                            {state.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="state"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Field
                      type="text"
                      id="zip"
                      name="zip"
                      className="form-control"
                      placeholder="Enter zip code"
                    />
                    <ErrorMessage
                      name="zip"
                      component="div"
                      className="error-message"
                    />
                  </div>

                  <div className="form-group">
                    <Button
                      variant="outline-success"
                      disabled={!isEnable || loading === "checking"}
                      size="lg"
                      className={`w-100 ${(!isEnable || loading === "checking") && styles.add_disable_btn}`}
                      type="submit"
                    >
                      {loading === "checking" && <Spinner size="sm" />}{" "}
                      {edit ? "Update" : "Add"} Address
                    </Button>
                  </div>

                  <Modal.Footer className="px-0">
                    <div className={styles.btn_container}>
                      <Button
                        onClick={onClose}
                        variant="secondary"
                        className={styles.cancel_btn}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="success"
                        className={styles.save_btn}
                        onClick={handleSave}
                        disabled={
                          addresses.length === 0 || loading === "saving"
                        }
                      >
                        {loading === "saving" && <Spinner size="sm" />}{" "}
                        {loading === "saving" ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </Modal.Footer>
                </form>
              </FormikProvider>
            </>
          )}
        </Modal.Body>
      </Modal>
      <Modal show={duplicateAddress} onHide={closeWarningModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Duplicate Address Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>An address with similar details already added for this policy.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="warning" onClick={closeWarningModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddressForm1;
